import { createTheme, alpha } from "@mui/material/styles";

const brand = {
  green: "#2E7D46",
  greenBright: "#438879",
  greenDark: "#1F5A31",
  greenSoft: "#E8F3EC",
};

const status = {
  ok: "#2E9E5B",
  warn: "#E4A11B",
  danger: "#D5405E",
};

const shape = { borderRadius: 12 };

const typography = {
  fontFamily: [
    "Inter",
    "-apple-system",
    "BlinkMacSystemFont",
    "Segoe UI",
    "Roboto",
    "Helvetica Neue",
    "Arial",
    "sans-serif",
  ].join(","),
  h4: { fontWeight: 700, letterSpacing: "-0.02em" },
  h5: { fontWeight: 700, letterSpacing: "-0.01em" },
  h6: { fontWeight: 600, letterSpacing: "-0.01em" },
  subtitle1: { fontWeight: 600 },
  subtitle2: { fontWeight: 600 },
  button: { fontWeight: 600, textTransform: "none", letterSpacing: 0 },
  body2: { lineHeight: 1.5 },
};

export const buildTheme = (mode) => {
  const isLight = mode === "light";

  const palette = {
    mode,
    primary: {
      main: brand.green,
      light: brand.greenBright,
      dark: brand.greenDark,
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: isLight ? "#334155" : "#94A3B8",
      contrastText: "#FFFFFF",
    },
    success: { main: status.ok },
    warning: { main: status.warn },
    error: { main: status.danger },
    info: { main: "#3B82C4" },
    background: {
      default: isLight ? "#F5F7F6" : "#101414",
      paper: isLight ? "#FFFFFF" : "#191D1E",
    },
    text: {
      primary: isLight ? "#1A2420" : "#E8EAEA",
      secondary: isLight ? "#5B6B63" : "#9BA3A2",
    },
    divider: isLight ? "#E4EAE6" : "#2A2F30",
    action: {
      hover: isLight ? alpha(brand.green, 0.05) : alpha(brand.greenBright, 0.10),
      selected: isLight ? brand.greenSoft : alpha(brand.greenBright, 0.16),
    },
  };

  const softShadow = isLight
    ? "0 1px 2px rgba(16,24,20,0.04), 0 4px 16px rgba(16,24,20,0.06)"
    : "0 1px 2px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.5)";

  return createTheme({
    palette,
    shape,
    typography,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { scrollbarColor: `${palette.divider} transparent` },
          "*::-webkit-scrollbar": { width: 10, height: 10 },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: palette.divider,
            borderRadius: 8,
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${palette.divider}`,
          },
          elevation1: { boxShadow: softShadow },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: alpha(palette.background.paper, 0.8),
            backdropFilter: "blur(12px)",
            color: palette.text.primary,
            borderBottom: `1px solid ${palette.divider}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: palette.background.paper,
            borderRight: `1px solid ${palette.divider}`,
            backgroundImage: "none",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, paddingInline: 16 },
          containedPrimary: {
            "&:hover": { backgroundColor: brand.greenDark },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            transition: "background-color .15s, color .15s",
          },
        },
      },
      MuiTextField: { defaultProps: { size: "small" } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isLight ? "#FBFCFB" : alpha("#000", 0.15),
            "& .MuiOutlinedInput-notchedOutline": { borderColor: palette.divider },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(brand.green, 0.4),
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${palette.divider}` },
          head: {
            fontWeight: 600,
            fontSize: "0.72rem",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: palette.text.secondary,
            backgroundColor: isLight ? "#FAFBFA" : "#1E2324",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:last-of-type td": { borderBottom: "none" },
            transition: "background-color .12s",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isLight ? "#1A2420" : "#1e4349",
            fontSize: "0.72rem",
            borderRadius: 8,
            padding: "6px 10px",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            marginInline: 8,
            "&.Mui-selected": {
              backgroundColor: palette.action.selected,
              "&:hover": { backgroundColor: palette.action.selected },
            },
          },
        },
      },
    },
  });
};

export { brand, status };